import type { ComponentType } from 'react'

import { BiTask } from 'react-icons/bi'
import { FaChartLine, FaHistory, FaSitemap } from 'react-icons/fa'
import {
  LuClipboardPaste,
  LuFileDiff,
  LuFlag,
  LuFolderTree,
  LuGitFork,
  LuGitCommitHorizontal,
  LuKeyRound,
  LuListChecks,
  LuMessagesSquare,
  LuPencil,
  LuShield,
  LuUsers,
} from 'react-icons/lu'
import { MdQueryStats } from 'react-icons/md'
import { RiGitMergeLine } from 'react-icons/ri'
import { TbReportMedical } from 'react-icons/tb'

import MarvinLogo from '@/components/pages/Assistant/shared/MarvinLogo'
import { NETWORK_DIFF_URL_BASE } from '@/constants'
import { ASSISTANT_NAME } from '@/utils/assistant/brand'

import { matchDigraph } from './networkRoutes'

type IconComponent = ComponentType<{ size?: number }>

type HrefContext = {
  networkId: string | undefined
  viewId: string | undefined
}

type SidebarItemLeaf = {
  key: string
  label: string
  ariaLabel: string
  icon: IconComponent
  hrefFor: (ctx: HrefContext) => string
  matchPath: (pathname: string, ctx: HrefContext) => boolean
}

export type SidebarItem = SidebarItemLeaf & {
  /**
   * Children appear indented beneath this item in expanded mode and as a flat
   * continuation of the icon list in rail mode. For the network-browser item,
   * children only render when the URL currently has a networkId (i.e. when the
   * user is inside a network). For a `collapsibleGroup` item they render when
   * the group is expanded, independent of the network context.
   */
  children?: SidebarItemLeaf[]
  /**
   * When true, the row is a section header that toggles its children open/
   * closed on click (with a chevron) instead of navigating. Used by the Admin
   * section; the network-browser's URL-gated children do not set this.
   */
  collapsibleGroup?: boolean
}

const viewIdQuery = (viewId: string | undefined): string =>
  viewId ? `?viewId=${viewId}` : ''

export const SIDEBAR_ITEMS: SidebarItem[] = [
  {
    key: 'network-browser',
    label: 'Network browser',
    ariaLabel: 'select-home-button',
    icon: LuFolderTree,
    hrefFor: () => '/',
    matchPath: (pathname) => pathname === '/',
    children: [
      {
        key: 'digraph',
        label: 'Digraph',
        ariaLabel: 'select-digraph-button',
        icon: FaSitemap,
        hrefFor: ({ networkId, viewId }) =>
          networkId ? `/${networkId}/${viewId ?? ''}` : '/',
        matchPath: (pathname, { networkId }) =>
          !!networkId && matchDigraph(pathname, networkId),
      },
      {
        key: 'history',
        label: 'History',
        ariaLabel: 'select-history-button',
        icon: FaHistory,
        hrefFor: ({ networkId, viewId }) =>
          `/${networkId}/history${viewIdQuery(viewId)}`,
        matchPath: (pathname, { networkId }) =>
          !!networkId && pathname.startsWith(`/${networkId}/history`),
      },
      {
        key: 'commits',
        label: 'Commits',
        ariaLabel: 'select-commits-button',
        icon: LuGitCommitHorizontal,
        hrefFor: ({ networkId }) => `/${networkId}/commits`,
        matchPath: (pathname, { networkId }) =>
          !!networkId && pathname.startsWith(`/${networkId}/commits`),
      },
      {
        key: 'diagnosis',
        label: 'Diagnosis',
        ariaLabel: 'select-diagnosis-button',
        icon: TbReportMedical,
        hrefFor: ({ networkId, viewId }) =>
          `/${networkId}/diagnosis${viewIdQuery(viewId)}`,
        matchPath: (pathname, { networkId }) =>
          !!networkId && pathname.startsWith(`/${networkId}/diagnosis`),
      },
      {
        key: 'assumption-set',
        label: 'Assumption Sets',
        ariaLabel: 'select-assumption-sets-button',
        icon: BiTask,
        hrefFor: ({ networkId, viewId }) =>
          `/${networkId}/assumption-set${viewIdQuery(viewId)}`,
        matchPath: (pathname, { networkId }) =>
          !!networkId &&
          pathname.startsWith(`/${networkId}/assumption-set`) &&
          !pathname.startsWith(`/${networkId}/assumption-set-diff`),
      },
      {
        key: 'pivoted-assumption-set',
        label: 'Pivoted Assumption Sets',
        ariaLabel: 'select-pivoted-assumption-sets-button',
        icon: LuClipboardPaste,
        hrefFor: ({ networkId, viewId }) =>
          `/${networkId}/pivoted-assumption-set${viewIdQuery(viewId)}`,
        matchPath: (pathname, { networkId }) =>
          !!networkId &&
          pathname.startsWith(`/${networkId}/pivoted-assumption-set`),
      },
      {
        key: 'network-diff',
        label: 'Network Diff',
        ariaLabel: 'select-network-diff-button',
        icon: RiGitMergeLine,
        hrefFor: ({ networkId }) => `/${networkId}/${NETWORK_DIFF_URL_BASE}`,
        matchPath: (pathname, { networkId }) =>
          !!networkId &&
          pathname.startsWith(`/${networkId}/${NETWORK_DIFF_URL_BASE}`),
      },
      {
        key: 'variable-types',
        label: 'Variable Types',
        ariaLabel: 'edit-variable-types-button',
        icon: LuPencil,
        hrefFor: ({ networkId, viewId }) =>
          `/${networkId}/variable-types${viewIdQuery(viewId)}`,
        matchPath: (pathname, { networkId }) =>
          !!networkId && pathname.startsWith(`/${networkId}/variable-types`),
      },
      {
        // KBE-855: a network-browser child like the others. Only meaningful for
        // a delta branch; the page self-explains on an ordinary network. Always
        // shown since KBE-882 un-flagged delta branches.
        key: 'delta-changes',
        label: 'Branch changes',
        ariaLabel: 'select-branch-changes-button',
        icon: LuFileDiff,
        hrefFor: ({ networkId, viewId }) =>
          `/${networkId}/delta-changes${viewIdQuery(viewId)}`,
        matchPath: (pathname, { networkId }) =>
          !!networkId && pathname.startsWith(`/${networkId}/delta-changes`),
      },
    ],
  },
  {
    key: 'network-tree',
    label: 'Network tree',
    ariaLabel: 'select-network-tree-button',
    icon: LuGitFork,
    hrefFor: () => '/network-tree',
    matchPath: (pathname) => pathname.startsWith('/network-tree'),
  },
  {
    key: 'optimization-jobs',
    label: 'Optimization Jobs',
    ariaLabel: 'select-optimization-jobs-button',
    icon: FaChartLine,
    hrefFor: () => '/optimization-jobs',
    matchPath: (pathname) => pathname.startsWith('/optimization-jobs'),
  },
  {
    key: 'collaboration',
    label: 'Collaboration',
    ariaLabel: 'select-collaboration-button',
    icon: LuUsers,
    collapsibleGroup: true,
    hrefFor: () => '/discussions',
    matchPath: (pathname) =>
      pathname.startsWith('/discussions') || pathname.startsWith('/issues'),
    children: [
      {
        key: 'discussions',
        label: 'Discussions',
        ariaLabel: 'select-discussions-button',
        icon: LuMessagesSquare,
        hrefFor: () => '/discussions',
        matchPath: (pathname) => pathname.startsWith('/discussions'),
      },
      {
        key: 'issues',
        label: 'Issues',
        ariaLabel: 'select-issues-button',
        icon: LuListChecks,
        hrefFor: () => '/issues',
        matchPath: (pathname) => pathname.startsWith('/issues'),
      },
    ],
  },
  {
    key: 'assistant',
    label: ASSISTANT_NAME,
    ariaLabel: 'select-assistant-button',
    icon: MarvinLogo,
    hrefFor: () => '/assistant',
    matchPath: (pathname) => pathname.startsWith('/assistant'),
  },
  {
    key: 'admin',
    label: 'Admin',
    ariaLabel: 'select-admin-button',
    icon: LuShield,
    collapsibleGroup: true,
    hrefFor: () => '/admin',
    matchPath: (pathname) => pathname.startsWith('/admin'),
    children: [
      {
        key: 'feature-flags',
        label: 'Feature flags',
        ariaLabel: 'select-feature-flags-button',
        icon: LuFlag,
        hrefFor: () => '/admin/flags',
        matchPath: (pathname) => pathname.startsWith('/admin/flags'),
      },
      {
        key: 'mcp-tokens',
        label: 'MCP tokens',
        ariaLabel: 'select-mcp-tokens-button',
        icon: LuKeyRound,
        hrefFor: () => '/admin/mcp-tokens',
        matchPath: (pathname) => pathname.startsWith('/admin/mcp-tokens'),
      },
      {
        key: 'performance',
        label: 'Performance',
        ariaLabel: 'select-performance-button',
        icon: MdQueryStats,
        hrefFor: () => '/performance',
        matchPath: (pathname) => pathname.startsWith('/performance'),
      },
    ],
  },
]
